import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  IconButton,
  Grid,
  Box,
  Typography,
  Paper,
  useTheme,
  Fade,
  Tooltip,
  ButtonGroup,
} from '@mui/material';
import {
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as ResetZoomIcon,
} from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';

const ImageModal = ({ open, onClose, images, currentImageIndex }) => {
  const [activeIndex, setActiveIndex] = useState(currentImageIndex || 0);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [lastTouchDistance, setLastTouchDistance] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);

  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const theme = useTheme();

  // Zoom configuration
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 5;
  const ZOOM_STEP = 0.2;

  // Reset zoom and position when image changes
  useEffect(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setIsZoomed(false);
    setIsDragging(false);
  }, [activeIndex]);

  // Update active index when currentImageIndex prop changes
  useEffect(() => {
    if (currentImageIndex !== undefined) {
      setActiveIndex(currentImageIndex);
    }
  }, [currentImageIndex]);

  // Update isZoomed state
  useEffect(() => {
    setIsZoomed(zoom > 1);
  }, [zoom]);

  // Navigation functions
  const goToPrevious = useCallback(() => {
    if (images && images.length > 0) {
      setActiveIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    }
  }, [images]);

  const goToNext = useCallback(() => {
    if (images && images.length > 0) {
      setActiveIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    }
  }, [images]);

  // Zoom functions
  const handleZoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
  }, []);

  const resetZoom = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  // Wheel zoom
  const handleWheel = useCallback(
    (event) => {
      if (!open) return;

      event.preventDefault();
      const delta = event.deltaY * -0.001;
      const newZoom = Math.min(Math.max(zoom + delta, MIN_ZOOM), MAX_ZOOM);

      if (newZoom !== zoom) {
        // Calculate zoom center point
        const rect = imageRef.current?.getBoundingClientRect();
        if (rect) {
          const centerX = (event.clientX - rect.left - rect.width / 2) / zoom;
          const centerY = (event.clientY - rect.top - rect.height / 2) / zoom;

          const zoomRatio = newZoom / zoom;
          setPosition((prev) => ({
            x: prev.x - centerX * (zoomRatio - 1),
            y: prev.y - centerY * (zoomRatio - 1),
          }));
        }

        setZoom(newZoom);
      }
    },
    [zoom, open]
  );

  // Touch zoom (pinch)
  const getTouchDistance = (touches) => {
    const touch1 = touches[0];
    const touch2 = touches[1];
    return Math.sqrt(
      Math.pow(touch2.clientX - touch1.clientX, 2) +
        Math.pow(touch2.clientY - touch1.clientY, 2)
    );
  };

  const handleTouchStart = useCallback(
    (event) => {
      if (event.touches.length === 2) {
        setLastTouchDistance(getTouchDistance(event.touches));
      } else if (event.touches.length === 1 && isZoomed) {
        const touch = event.touches[0];
        setIsDragging(true);
        setDragStart({
          x: touch.clientX - position.x,
          y: touch.clientY - position.y,
        });
      }
    },
    [isZoomed, position]
  );

  const handleTouchMove = useCallback(
    (event) => {
      event.preventDefault();

      if (event.touches.length === 2) {
        const currentDistance = getTouchDistance(event.touches);
        if (lastTouchDistance > 0) {
          const scale = currentDistance / lastTouchDistance;
          const newZoom = Math.min(Math.max(zoom * scale, MIN_ZOOM), MAX_ZOOM);
          setZoom(newZoom);
        }
        setLastTouchDistance(currentDistance);
      } else if (event.touches.length === 1 && isDragging && isZoomed) {
        const touch = event.touches[0];
        setPosition({
          x: touch.clientX - dragStart.x,
          y: touch.clientY - dragStart.y,
        });
      }
    },
    [zoom, lastTouchDistance, isDragging, isZoomed, dragStart]
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    setLastTouchDistance(0);
  }, []);

  // Mouse drag for panning
  const handleMouseDown = useCallback(
    (event) => {
      if (isZoomed && event.button === 0) {
        setIsDragging(true);
        setDragStart({
          x: event.clientX - position.x,
          y: event.clientY - position.y,
        });
        event.preventDefault();
      }
    },
    [isZoomed, position]
  );

  const handleMouseMove = useCallback(
    (event) => {
      if (isDragging && isZoomed) {
        setPosition({
          x: event.clientX - dragStart.x,
          y: event.clientY - dragStart.y,
        });
      }
    },
    [isDragging, isZoomed, dragStart]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Double click to zoom
  const handleDoubleClick = useCallback(
    (event) => {
      if (zoom === 1) {
        // Zoom in to 2x at the clicked point
        const rect = imageRef.current?.getBoundingClientRect();
        if (rect) {
          const centerX = event.clientX - rect.left - rect.width / 2;
          const centerY = event.clientY - rect.top - rect.height / 2;
          setPosition({ x: -centerX, y: -centerY });
          setZoom(2);
        }
      } else {
        resetZoom();
      }
    },
    [zoom, resetZoom]
  );

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (!open) return;

      switch (event.key) {
        case 'ArrowLeft':
          if (!isZoomed) {
            event.preventDefault();
            goToPrevious();
          }
          break;
        case 'ArrowRight':
          if (!isZoomed) {
            event.preventDefault();
            goToNext();
          }
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
        case '+':
        case '=':
          event.preventDefault();
          handleZoomIn();
          break;
        case '-':
          event.preventDefault();
          handleZoomOut();
          break;
        case '0':
          event.preventDefault();
          resetZoom();
          break;
        default:
          break;
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [
    open,
    goToPrevious,
    goToNext,
    onClose,
    handleZoomIn,
    handleZoomOut,
    resetZoom,
    isZoomed,
  ]);

  // Mouse event listeners
  useEffect(() => {
    const handleGlobalMouseMove = (event) => handleMouseMove(event);
    const handleGlobalMouseUp = () => handleMouseUp();

    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Don't render if no images or invalid index
  if (
    !images ||
    images.length === 0 ||
    activeIndex < 0 ||
    activeIndex >= images.length
  ) {
    return null;
  }

  const currentImage = images[activeIndex];
  const hasMultipleImages = images.length > 1;

  // Extract image information
  const getImageInfo = (image) => {
    let imageName = 'Nombre no disponible';
    if (image.src) {
      const srcParts = image.src.split('?')[0].split('/');
      const fileNameWithExtension = srcParts[srcParts.length - 1];
      if (fileNameWithExtension) {
        imageName = fileNameWithExtension.replace(/[^a-zA-Z0-9_.-]/g, '_');
      }
    }

    const [latitude, longitude] = image.meta_loc
      ? image.meta_loc.split(', ')
      : ['N/A', 'N/A'];

    const formattedDate = image.meta_date
      ? new Date(image.meta_date).toLocaleDateString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      : null;

    return { imageName, latitude, longitude, formattedDate };
  };

  const { imageName, latitude, longitude, formattedDate } =
    getImageInfo(currentImage);

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Fade in={open}>
        {/* Main Image Container */}
        <Box
          ref={containerRef}
          sx={{
            position: 'fixed',
            left: '16vw',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            maxWidth: '37vw',
            maxHeight: '95vh',
            borderRadius: '20px',
            overflow: 'hidden',
            cursor: isZoomed ? (isDragging ? 'grabbing' : 'grab') : 'default',
          }}
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Close Button */}
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.6)',
              zIndex: 10,
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.8)',
              },
            }}
          >
            <CloseIcon
              sx={{ color: 'white', width: '0.7em', height: '0.7em' }}
            />
          </IconButton>

          {/* Zoom Controls */}
          <Paper
            elevation={3}
            sx={{
              position: 'absolute',
              top: 16,
              left: '53%',
              transform: 'translateX(-50%)',
              bgcolor: 'rgba(0, 0, 0, 0.7)',
              zIndex: 10,
              borderRadius: 2,
            }}
          >
            <ButtonGroup variant='text' sx={{ color: 'white' }}>
              <Tooltip title='Alejar'>
                <IconButton
                  onClick={handleZoomOut}
                  disabled={zoom <= MIN_ZOOM}
                  sx={{
                    color: 'white',
                    '&:disabled': { color: 'rgba(255, 255, 255, 0.3)' },
                  }}
                >
                  <ZoomOutIcon
                    sx={{ color: 'white', width: '0.7em', height: '0.7em' }}
                  />
                </IconButton>
              </Tooltip>

              <Paper
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 2,
                  bgcolor: 'transparent',
                  color: 'white',
                  minWidth: 60,
                  justifyContent: 'center',
                }}
              >
                <Typography variant='body2'>
                  {Math.round(zoom * 100)}%
                </Typography>
              </Paper>

              <Tooltip title='Acercar'>
                <IconButton
                  onClick={handleZoomIn}
                  disabled={zoom >= MAX_ZOOM}
                  sx={{
                    color: 'white',
                    '&:disabled': { color: 'rgba(255, 255, 255, 0.3)' },
                  }}
                >
                  <ZoomInIcon
                    sx={{ color: 'white', width: '0.7em', height: '0.7em' }}
                  />
                </IconButton>
              </Tooltip>

              <Tooltip title='Restablecer zoom (100%)'>
                <IconButton
                  onClick={resetZoom}
                  disabled={zoom === 1 && position.x === 0 && position.y === 0}
                  sx={{
                    color: 'white',
                    '&:disabled': { color: 'rgba(255, 255, 255, 0.3)' },
                  }}
                >
                  <ResetZoomIcon
                    sx={{ color: 'white', width: '0.7em', height: '0.7em' }}
                  />
                </IconButton>
              </Tooltip>
            </ButtonGroup>
          </Paper>

          {/* Image Counter */}
          {hasMultipleImages && (
            <Paper
              elevation={3}
              sx={{
                position: 'absolute',
                top: 20,
                left: 16,
                px: 2,
                py: 0.5,
                bgcolor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                zIndex: 10,
                borderRadius: 2,
              }}
            >
              <Typography variant='body2'>
                {activeIndex + 1} de {images.length}
              </Typography>
            </Paper>
          )}

          {/* Left Arrow */}
          {hasMultipleImages && !isZoomed && (
            <IconButton
              onClick={goToPrevious}
              sx={{
                position: 'absolute',
                left: 15,
                color: 'white',
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1,
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                },
              }}
            >
              <FontAwesomeIcon icon={faChevronLeft} width={24} />
            </IconButton>
          )}

          {/* Image */}
          <img
            ref={imageRef}
            src={currentImage.src}
            alt={`Imagen ${activeIndex + 1}`}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              boxShadow: theme.shadows[24],
              transform: `scale(${zoom}) translate(${position.x / zoom}px, ${
                position.y / zoom
              }px)`,
              transition: isDragging ? 'none' : 'transform 0.2s ease-out',
              userSelect: 'none',
              pointerEvents: isZoomed ? 'auto' : 'none',
            }}
            onMouseDown={handleMouseDown}
            onDoubleClick={handleDoubleClick}
            onError={(e) => {
              e.target.src =
                'https://via.placeholder.com/400?text=No+Disponible';
              e.target.alt = 'Imagen no disponible';
            }}
            onDragStart={(e) => e.preventDefault()}
          />

          {/* Modern Glass Overlay Info Panel */}
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              right: 0,
              width: '30vw',
              height: '100%',
              background:
                'linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.4) 70%, transparent 100%)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              padding: '24px 20px 16px 20px',
              color: 'white',
              zIndex: 3,
              transition: 'all 0.3s ease-in-out',
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography
                  variant='body2'
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    color: 'rgba(255, 255, 255, 0.95)',
                    wordBreak: 'break-all',
                    lineHeight: 1.3,
                    fontSize: '1.1rem',
                  }}
                >
                  {imageName}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 0.5 }}
                >
                  {formattedDate && (
                    <Typography
                      variant='caption'
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontSize: '0.9rem',
                      }}
                    >
                      📅 {formattedDate}
                    </Typography>
                  )}

                  {latitude !== 'N/A' && longitude !== 'N/A' && (
                    <Typography
                      variant='caption'
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontSize: '0.9rem',
                      }}
                    >
                      📍 {parseFloat(latitude).toFixed(4)},{' '}
                      {parseFloat(longitude).toFixed(4)}
                    </Typography>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant='caption'
                  sx={{
                    position: 'fixed',
                    bottom: 16,
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '0.7rem',
                    fontStyle: 'italic',
                  }}
                >
                  {hasMultipleImages && !isZoomed && (
                    <>
                      ← → para navegar
                      <br />
                    </>
                  )}
                  {isZoomed && (
                    <>
                      Click y arrastra para mover
                      <br />
                      Doble click para resetear
                      <br />
                    </>
                  )}
                  Rueda del ratón para zoom
                  <br />
                  ESC para cerrar
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Right Arrow */}
          {hasMultipleImages && !isZoomed && (
            <IconButton
              onClick={goToNext}
              sx={{
                position: 'absolute',
                right: 15,
                color: 'white',
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 5,
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                },
              }}
            >
              <FontAwesomeIcon icon={faChevronRight} width={24} />
            </IconButton>
          )}
        </Box>
      </Fade>
    </Modal>
  );
};

export default ImageModal;
